import { useState, useEffect, useCallback } from 'react'
import { User, LoginResponse } from '../types'
import { authAPI, userAPI } from '../services/api'
import { mergeLoginUserWithServerProfile, hydrateUserFromServer } from '../utils/userMerge'
import { isJwtNotExpired, jwtExpiryToIsoString } from '../utils/jwtClient'

/** 本地 Token 是否仍有效（优先 token_expire_time，缺失时用 JWT exp） */
export function isStoredSessionValid(): boolean {
  try {
    const token = localStorage.getItem('token')
    if (!token) return false
    const expire = localStorage.getItem('token_expire_time')
    if (expire) {
      const expireDate = new Date(expire)
      if (!Number.isNaN(expireDate.getTime()) && expireDate > new Date()) {
        return true
      }
    }
    return isJwtNotExpired(token)
  } catch {
    return false
  }
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  // 检查Token是否过期
  const isTokenExpired = (expireTime: string): boolean => {
    try {
      const expireDate = new Date(expireTime)
      const now = new Date()
      return now >= expireDate
    } catch (error) {
      console.error('解析Token过期时间失败:', error)
      return true
    }
  }

  // 检查本地存储的用户信息
  useEffect(() => {
    const checkAuth = () => {
      try {
        const storedUser = localStorage.getItem('user')
        const storedToken = localStorage.getItem('token')
        const storedExpireTime = localStorage.getItem('token_expire_time')

        const clearSession = () => {
          localStorage.removeItem('user')
          localStorage.removeItem('token')
          localStorage.removeItem('token_expire_time')
          setUser(null)
          setIsAuthenticated(false)
        }

        // 无 Token 则视为未登录（勿因缺少 user 或 token_expire_time 误清另一字段）
        if (!storedToken) {
          if (storedUser || storedExpireTime) {
            localStorage.removeItem('user')
            localStorage.removeItem('token_expire_time')
          }
          setUser(null)
          setIsAuthenticated(false)
          return
        }

        // 有过期时间则优先用；否则用 JWT exp，避免旧数据只有 token+user 被整站清空
        let sessionExpired = false
        if (storedExpireTime) {
          sessionExpired = isTokenExpired(storedExpireTime)
        } else {
          const iso = jwtExpiryToIsoString(storedToken)
          if (iso) {
            localStorage.setItem('token_expire_time', iso)
          }
          sessionExpired = !isJwtNotExpired(storedToken)
        }

        if (sessionExpired) {
          console.log('🔐 Token已过期，清除本地存储')
          clearSession()
          return
        }

        if (storedUser) {
          try {
            const userData = JSON.parse(storedUser) as User
            setUser(userData)
            setIsAuthenticated(true)
            console.log('✅ Token有效，用户已登录')
          } catch {
            // JSON 损坏时勿清空 Token，保留会话并由下方 hydrate 从服务端拉回用户资料
            console.warn('⚠️ user 缓存 JSON 损坏，将保留 Token 并从服务端恢复资料')
            localStorage.removeItem('user')
            setUser(null)
            setIsAuthenticated(true)
          }
        } else {
          // 有 Token、无 user 缓存：仍视为已登录，由 hydrate 拉取
          console.log('ℹ️ 有有效 Token 但无 user 缓存，将从服务端拉取用户资料')
          setUser(null)
          setIsAuthenticated(true)
        }
      } catch (error) {
        console.error('解析用户信息失败:', error)
        localStorage.removeItem('user')
        localStorage.removeItem('token')
        localStorage.removeItem('token_expire_time')
        setUser(null)
        setIsAuthenticated(false)
      } finally {
        setLoading(false)
      }
    }

    checkAuth()
  }, [refreshTrigger])

  // 会话有效时以服务端为准同步 user（库表为真源，避免本地陈旧/损坏导致「像被清空」）
  useEffect(() => {
    if (loading || !isAuthenticated) return

    let cancelled = false
    ;(async () => {
      try {
        const cur = (await userAPI.getCurrentUser()) as {
          success?: boolean
          data?: User
        }
        if (cancelled || !cur.success || !cur.data) return
        const localRaw = localStorage.getItem('user')
        let localUser: User | null = null
        if (localRaw) {
          try {
            localUser = JSON.parse(localRaw) as User
          } catch {
            localUser = null
          }
        }
        const merged = hydrateUserFromServer(localUser, cur.data as User)
        setUser(merged)
        localStorage.setItem('user', JSON.stringify(merged))
      } catch (e) {
        console.warn('启动时同步用户资料失败（可稍后重试）:', e)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [loading, isAuthenticated, refreshTrigger])

  // 登录
  const login = useCallback(async (code: string) => {
    try {
      setLoading(true)
      console.log('🔐 开始企业微信登录，code:', code)
      console.log('🌐 API地址:', import.meta.env.VITE_API_BASE_URL)
      const response = await authAPI.wechatLogin(code) as unknown as LoginResponse
      
      if (response.success) {
        // 先写入 Token，便于随后用服务端完整资料覆盖（以库为准，避免登录接口与库短暂不一致）
        if (response.token) {
          localStorage.setItem('token', response.token)
          let expIso = response.token_expire_time
          if (!expIso) {
            expIso = jwtExpiryToIsoString(response.token) ?? undefined
          }
          if (expIso) {
            localStorage.setItem('token_expire_time', expIso)
          }
        } else if (response.token_expire_time) {
          localStorage.setItem('token_expire_time', response.token_expire_time)
        }

        let mergedUser = response.user as User
        try {
          const cur = (await userAPI.getCurrentUser()) as {
            success?: boolean
            data?: User
          }
          if (cur.success && cur.data) {
            mergedUser = mergeLoginUserWithServerProfile(
              response.user as User,
              cur.data
            )
          }
        } catch (e) {
          console.warn('登录后拉取用户信息失败，使用登录接口返回:', e)
        }

        setUser(mergedUser)
        setIsAuthenticated(true)
        localStorage.setItem('user', JSON.stringify(mergedUser))

        console.log('🔐 登录成功，Token已保存')
        return { success: true, user: mergedUser }
      } else {
        return { success: false, message: response.message }
      }
    } catch (error: any) {
      console.error('❌ 登录失败:', error)
      console.error('❌ 错误详情:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      })
      return { 
        success: false, 
        message: error.response?.data?.message || '登录失败，请重试' 
      }
    } finally {
      setLoading(false)
    }
  }, [])

  // 登出
  const logout = useCallback(() => {
    setUser(null)
    setIsAuthenticated(false)
    localStorage.removeItem('user')
    localStorage.removeItem('token')
    localStorage.removeItem('token_expire_time')
    console.log('🔐 用户已登出，清除所有Token信息')
  }, [])

  // 更新用户信息
  const updateUser = useCallback((updatedUser: User) => {
    setUser(updatedUser)
    localStorage.setItem('user', JSON.stringify(updatedUser))
  }, [])

  // 刷新认证状态
  const refreshAuth = useCallback(() => {
    console.log('🔄 手动刷新认证状态')
    setRefreshTrigger(prev => prev + 1)
  }, [])

  return {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    updateUser,
    refreshAuth
  }
}
